import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  TextField,
  Button,
  Box,
  CardContent,
  Card,
  MenuItem,
  Stack,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { getLog } from "../../api/topicDataAPI";
import { useLocation } from "react-router-dom";
import { getNodeById } from "../../api/nodeAPI";
import { exportLogs } from "../../utils/logsExporter";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";
import { useSnackbar } from "../../hooks/useSnackbar";
import { getCurrentTenant } from "../../config/tenantConfig";
import DateRangePresetButtons from "../../components/DateRangePresetButtons";
import { getDateRangePreset } from "../../utils/dateRangePresets";

// dtype , subject, severity, start, end, pagesize
// page start with 1
// pagesize default 20

export default function Logs() {
  const { t } = useTranslation();
  const location = useLocation();

  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;
  const projectCode = getCurrentTenant().projectCode;
  const shouldLeftAlignGrid = projectCode === "RCU" || projectCode === "VENDAI";

  const columns = [
    {
      field: "subject",
      headerName: t("nodeComponents.logs.columns.subject"),
      flex: 0.8,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "severity",
      headerName: t("nodeComponents.logs.columns.severity"),
      flex: 0.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "message",
      headerName: t("nodeComponents.logs.columns.message"),
      flex: 2,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              lineHeight: "normal",
              whiteSpace: "normal",
              wordBreak: "break-word",
              padding: "8px",
              textAlign: shouldLeftAlignGrid ? "left" : "center",
              overflow: "visible",
            }}
          >
            {params.value}
          </div>
        );
      },
      cellClassName: "message-cell",
    },
    {
      field: "epoch",
      headerName: t("nodeComponents.logs.columns.time"),
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
  ];

  const [rows, setRows] = useState([]);
  const [allLogs, setAllLogs] = useState([]); // Tüm logları saklamak için yeni state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [severity, setSeverity] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [messageFilter, setMessageFilter] = useState(""); // Yeni message filter state
  const [loading, setLoading] = useState(false);
  const [nodeName, setNodeName] = useState("Node"); // Node ismi için state
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  // Fetch node name
  useEffect(() => {
    const fetchNodeName = async () => {
      if (!node_id) return;
      try {
        const data = await getNodeById(node_id);
        if (data) {
          setNodeName(data.name || "Node");
        }
      } catch (err) {
        console.error("Failed to fetch node name:", err);
      }
    };

    fetchNodeName();
  }, [node_id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const logsData = await getLog(node_id);
        const safeLogsData = Array.isArray(logsData) ? logsData : [];
        setAllLogs(safeLogsData);
        setRows(
          safeLogsData.map((log) => ({
            ...log,
            id: log._id || `temp-${Date.now()}-${Math.random()}`,
          }))
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [node_id]);

  // Hızlı tarih aralığı butonları state güncellemesini beklemeden filtrelemeli,
  // bu yüzden tarihler parametre olarak da verilebiliyor.
  function applyFilters(from = startDate, to = endDate) {
    let filteredLogs = Array.isArray(allLogs) ? allLogs : [];

    if (filteredLogs.length > 0 && from) {
      const start = new Date(`${from}T00:00:00`);
      filteredLogs = filteredLogs.filter((log) => new Date(log.epoch) >= start);
    }
    if (filteredLogs.length > 0 && to) {
      const end = new Date(`${to}T23:59:59`);
      filteredLogs = filteredLogs.filter((log) => new Date(log.epoch) <= end);
    }
    if (filteredLogs.length > 0 && severity) {
      filteredLogs = filteredLogs.filter(
        (log) => String(log.severity) === String(severity)
      );
    }
    if (filteredLogs.length > 0 && subjectFilter) {
      const subjectKeywords = subjectFilter
        .split(",")
        .map((k) => k.trim().toLowerCase());
      filteredLogs = filteredLogs.filter((log) => {
        if (!log.subject) return false;
        const subjectArray = Array.isArray(log.subject)
          ? log.subject
          : [String(log.subject)];
        return subjectKeywords.some((keyword) =>
          subjectArray.some((sub) => sub.toLowerCase().includes(keyword))
        );
      });
    }
    if (filteredLogs.length > 0 && messageFilter) {
      const messageKeywords = messageFilter
        .split(",")
        .map((k) => k.trim().toLowerCase());
      filteredLogs = filteredLogs.filter((log) => {
        if (!log.message) return false;
        return messageKeywords.some((keyword) =>
          String(log.message).toLowerCase().includes(keyword)
        );
      });
    }
    setRows(
      filteredLogs.map((log) => ({
        ...log,
        id: log._id || `temp-${Date.now()}-${Math.random()}`,
      }))
    );
  }

  const handleFilterClick = () => applyFilters();

  const handlePresetSelect = (presetKey) => {
    const { start, end } = getDateRangePreset(presetKey);
    setStartDate(start);
    setEndDate(end);
    applyFilters(start, end);
  };

  // Refresh handler
  async function handleRefresh() {
    setLoading(true);
    try {
      const logsData = await getLog(node_id);
      const safeLogsData = Array.isArray(logsData) ? logsData : [];
      setAllLogs(safeLogsData);
      setRows(
        safeLogsData.map((log) => ({
          ...log,
          id: log._id || `temp-${Date.now()}-${Math.random()}`,
        }))
      );
    } finally {
      setLoading(false);
    }
  }

  // Excel export handler
  function handleExport() {
    try {
      const result = exportLogs(rows, columns, nodeName);

      if (result.success) {
        showSnackbar(t('nodeComponents.logs.exportSuccess', { fileName: result.fileName }), 'success');
      } else {
        showSnackbar(result.error || t('nodeComponents.logs.exportError'), 'error');
      }
    } catch (error) {
      console.error('Excel export error:', error);
      showSnackbar(t('nodeComponents.logs.downloadError'), 'error');
    }
  }

  // VendAI and RCU: left-align every header + cell.
  const displayColumns = shouldLeftAlignGrid
    ? columns.map((c) => ({ ...c, align: "left", headerAlign: "left" }))
    : columns;

  return (
    <>
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
                  textAlign: "left",
                }}
              >
                {t("nodeComponents.logs.title")}
              </Typography>
              <Box sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }} />
              <DateRangePresetButtons onSelect={handlePresetSelect} />
            </Stack>

            {/* Tüm filtreler + aksiyon butonları tek satırda; dar ekranda sarar. */}
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <TextField
                select
                label={t("nodeComponents.logs.logLevelFilter")}
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: "0 1 110px", minWidth: 100 }}
              >
                <MenuItem value="">{t("nodeComponents.logs.all")}</MenuItem>
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4">4</MenuItem>
                <MenuItem value="5">5</MenuItem>
              </TextField>
              <TextField
                label={t("nodeComponents.logs.subjectFilter")}
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: "1 1 150px", minWidth: 130 }}
              />
              <TextField
                label={t("nodeComponents.logs.messageFilter")}
                value={messageFilter}
                onChange={(e) => setMessageFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: "1 1 150px", minWidth: 130 }}
              />
              <TextField
                label={t("nodeComponents.logs.startDate")}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: "0 1 160px", minWidth: 150 }}
              />
              <TextField
                label={t("nodeComponents.logs.endDate")}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: "0 1 160px", minWidth: 150 }}
              />
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  color="third"
                  onClick={handleFilterClick}
                  startIcon={<FilterAltIcon />}
                  disabled={loading}
                  sx={{ height: 40, whiteSpace: "nowrap", px: 2 }}
                >
                  {t("nodeComponents.logs.filter")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRefresh}
                  disabled={loading}
                  aria-label={t("nodeComponents.logs.refresh")}
                  title={t("nodeComponents.logs.refresh")}
                  sx={{ height: 40, minWidth: 40, width: 40, p: 0 }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleExport}
                  disabled={loading}
                  aria-label={t("nodeComponents.logs.export")}
                  title={t("nodeComponents.logs.export")}
                  sx={{ height: 40, minWidth: 40, width: 40, p: 0 }}
                >
                  <FileDownloadIcon />
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "& .MuiDataGrid-root": {
            minWidth: "800px", // Ensures the DataGrid doesn't shrink too much
          },
        }}
      >
        {" "}
        <DataGrid
          columns={displayColumns}
          rows={rows}
          getRowId={(row) => row.id}
          pagination
          loading={loading}
          initialState={{
            sorting: { sortModel: [{ field: "epoch", sort: "desc" }] },
            pagination: {
              paginationModel: { pageSize: 20 },
            },
          }}
          pageSizeOptions={[20]}
          disableSelectionOnClick
          getRowHeight={() => "auto"}
          getEstimatedRowHeight={() => 100}
          sx={{
            width: "100%" /* DataGrid genişliğini tam ayarla */,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
            "& .MuiDataGrid-cell": {
              alignItems: "center",
              display: "flex",
              whiteSpace: "normal",
              lineHeight: "normal",
              padding: "8px",
            },
            "& .MuiDataGrid-cell.message-cell": {
              minHeight: "unset !important",
              maxHeight: "unset !important",
              height: "auto !important",
              alignItems: "flex-start",
              whiteSpace: "normal",
              overflow: "visible",
            },
            "& .MuiDataGrid-row": {
              maxHeight: "unset !important",
              minHeight: "48px !important",
            },
            "& .MuiDataGrid-main": {
              width: "100%",
              overflow: "auto",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto",
            },
            /* DataGrid horizontally scrollable */
            "& .MuiDataGrid-root": {
              minWidth: "800px",
            },
            "& .MuiDataGrid-columnHeader": {
              minWidth: "100px" /* Başlıklar için minimum genişlik */,
            },
          }}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
